import { useId } from 'react';
import styled, { css } from 'styled-components';
import { Button, Text } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import { RoomMainAmenitiesInline } from '@/app/components/hotel-detail/rooms/RoomMainAmenities';
import { MealPlanOptionsByMealPlan } from '@/app/components/hotel-detail/rooms/RoomPanelMealPlanOptions';
import ImageWithFallback, { DEFAULT_FALLBACK_IMAGE } from '@/app/components/hotel-detail/ImageWithFallback';
import { keyUpHandler } from '@/app/components/hotel-detail/common/common.helper';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { lastminuteDealsGradientOnButton } from '@/app/common/components/Promotions/styled';
import { Icon } from '@/app/common/icons/Icon';
import { MealPlanOptionsGroups, RoomsIncludedInPrice } from './RoomPanelCommon';
import { RoomMealPlanAndCancellationDetails } from './RoomMealPlanAndCancellationDetails';
import { RoomPanelPrice } from './RoomPanelPrice';
import { useFetchHotelStatic } from '../hooks/useFetchHotelStatic';
import { RoomAvailabilityAlertLabel } from './RoomAvailabilityAlertLabel';
import { SubtleButton } from '../common/button';
import { getMainAmenities, hasFlashSalePromotion, hasRoomDetails } from './roomCommon';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { useRoomPanelOpenModal } from './RoomPanelModalProvider';
import { getSingleSource, transformationMobileRoomPanelLayout } from '../getGallerySourceOptimization';
import { getRoomImageFallbackReason, RoomImageFallbackCaption } from './RoomImageFallbackCaption';
import { useLoadableImages } from '../hooks/useLoadableImages';

const StyledButton = styled(Button)<{ $isLmnDeal?: boolean }>`
  ${(p) => p.$isLmnDeal && lastminuteDealsGradientOnButton};
`;

type RoomPanelMobileProps = {
  room: RoomType;
  onRoomClick: () => void;
  selectedMealPlan: MealPlanType;
  onContinueClick: (mealPlan: MealPlanType) => void;
  onSelectMealPlan: (mealPlan: MealPlanType) => void;
  isBestValue?: boolean;
};

export function RoomPanelMobile(props: RoomPanelMobileProps) {
  const { room, onRoomClick, selectedMealPlan, onContinueClick, onSelectMealPlan, isBestValue } = props;

  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { openModal } = useRoomPanelOpenModal();
  const { data: hotelStaticData } = useFetchHotelStatic();
  const { accommodationOnly } = useUrlSearchParams();

  const mainAmenities = getMainAmenities(t, room);
  const hasDetails = hasRoomDetails(room);

  const showLastMinuteDeals = localization?.configValuesApp?.lastMinuteDeals === true;
  const isLmnDeal = showLastMinuteDeals && hasFlashSalePromotion(selectedMealPlan);
  const taxesIncludedDp = !!(
    localization?.configValuesApp?.taxesIncludedPopup || localization?.configValuesApp?.taxesIncludedDoublePricing
  );

  const extendedRoomPanel = localization?.configValuesApp?.mobileShowAllRooms || false;

  const hasImages = room.images.length > 0;
  // MPH-1321: the card shows the room's first photo that loads, each one that fails giving its place to the
  // next, and its counter counts only the photos left. The photos that failed are shared with the room
  // details gallery, so the card and the gallery show the same ones.
  const { loadableImages, onImageFallback } = useLoadableImages(room.images);
  const photosFailed = hasImages && loadableImages.length === 0;
  const captionReason = getRoomImageFallbackReason({
    hasImages,
    photosFailed,
    isTopSecret: !!hotelStaticData?.isTopSecret,
  });
  const captionId = useId();

  const shownImage: string | undefined = loadableImages[0];
  const shownSrc = shownImage
    ? getSingleSource(shownImage, transformationMobileRoomPanelLayout)
    : DEFAULT_FALLBACK_IMAGE;

  const onMobileImageClick = () => {
    openModal(room);
  };

  return (
    <div
      css={`
        background-color: white;
        display: flex;
        flex-direction: column;
        border-radius: 8px;
        height: 100%;
      `}
    >
      {!hotelStaticData?.isTopSecret && (
        <div
          css={`
            width: 100%;
            height: 144px;
            position: relative;
          `}
          role="button"
          tabIndex={0}
          onClick={onMobileImageClick}
          onKeyUp={keyUpHandler(onMobileImageClick)}
          aria-label={`${t('vacation.select.rooms.showdetails')} - ${room.name}`}
          aria-describedby={captionReason ? captionId : undefined}
        >
          <ImageWithFallback
            key={shownSrc}
            url={[shownSrc]}
            alt="room image"
            loading="lazy"
            onFallback={shownImage ? () => onImageFallback(shownImage, shownSrc) : undefined}
            css={css`
              border-radius: 8px;
              width: 100%;
              height: 144px;
              object-fit: cover;
              -webkit-tap-highlight-color: transparent;
            `}
          />
          {captionReason && <RoomImageFallbackCaption id={captionId} reason={captionReason} />}
          {/* MPH-1321: counts only the photos left to show. */}
          {loadableImages.length > 1 ? (
            <div
              css={css`
                align-items: center;
                background: rgb(61 61 61 / 50%);
                border-radius: 4px;
                bottom: 8px;
                color: ${(p) => p.theme.kallisto.color.text.static.textOnDark};
                display: flex;
                font-size: ${(p) => p.theme.kallisto.font.size['150']};
                gap: 8px;
                height: 24px;
                padding: 0 8px;
                position: absolute;
                right: 8px;
              `}
            >
              <Icon category="UI Essentials" name="icon-photo-gallery" size={14} color="iconOnDark" />
              <span>{loadableImages.length}</span>
            </div>
          ) : undefined}
        </div>
      )}

      <div
        {...(!extendedRoomPanel
          ? { role: 'button', tabIndex: 0, onClick: onRoomClick, onKeyUp: keyUpHandler(onRoomClick) }
          : undefined)}
        css={css`
          width: 100%;
          height: 100%;
          display: flex;
          padding: 12px 16px 16px;
          flex-direction: column;
          gap: 12px;
        `}
      >
        <div
          css={`
            display: flex;
            flex-direction: column;
            gap: 4px;
            flex-grow: 1;
          `}
        >
          <RoomMobileHeader
            roomName={room.name}
            roomsIncludedInPrice={room.roomsIncludedInPrice}
            roomsLeft={room.configurations[room.selectedMealPlanIndex].roomsLeft}
          />
          <div
            css={`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            {mainAmenities.length > 0 || hasDetails ? (
              <div>
                {mainAmenities.length > 0 && (
                  <div
                    css={css`
                      color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
                      ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
                      line-height: 24px;
                    `}
                  >
                    <RoomMainAmenitiesInline amenities={mainAmenities.slice(0, 4)} />
                  </div>
                )}
                {hasDetails && (
                  <SubtleButton
                    css={`
                      padding: 2px 0;
                    `}
                    {...(extendedRoomPanel ? { onClick: onRoomClick, onKeyUp: keyUpHandler(onRoomClick) } : undefined)}
                  >
                    <Text variant="BodySmall">{t('vacation.select.rooms.showdetails')}</Text>
                  </SubtleButton>
                )}
              </div>
            ) : undefined}
            {extendedRoomPanel ? (
              <>
                <Separator
                  css={`
                    width: auto;
                    margin: 8px -16px;
                  `}
                />
                <MealPlanOptionsByMealPlan
                  context="room-panel"
                  room={room}
                  selectedMealPlan={selectedMealPlan}
                  variation="single-column"
                  onContinueClick={onContinueClick}
                  onSelectMealPlan={onSelectMealPlan}
                  css={`
                    padding-bottom: 4px;
                    row-gap: 0;

                    ${MealPlanOptionsGroups} {
                      border: 0;
                      padding: 0;
                    }

                    hr {
                      margin: 16px -16px;
                    }
                  `}
                />
              </>
            ) : (
              <RoomMealPlanAndCancellationDetails
                mealPlans={room.configurations}
                selectedMealPlanIndex={room.selectedMealPlanIndex || 0}
                roomId={room.roomId}
              />
            )}
          </div>
        </div>
        {!extendedRoomPanel && (
          <Separator
            css={`
              flex-grow: 0;
            `}
          />
        )}
        <RoomPanelPrice
          hideTaxes={taxesIncludedDp || (isBestValue && !extendedRoomPanel)}
          hideLoyaltyBadge={!accommodationOnly}
          mealPlan={selectedMealPlan}
          roomsIncludedInPrice={room.roomsIncludedInPrice}
          showPriceDifference={!extendedRoomPanel}
          roomId={room.roomId}
          css={`
            justify-content: flex-end;
            flex-grow: 0;
            text-align: right;
          `}
        />
        <StyledButton
          variant="primary"
          data-testid="continue-button"
          $isLmnDeal={isLmnDeal}
          {...(extendedRoomPanel
            ? {
                onClick: () => onContinueClick(selectedMealPlan),
                onKeyUp: keyUpHandler(() => onContinueClick(selectedMealPlan)),
              }
            : undefined)}
        >
          {accommodationOnly && extendedRoomPanel
            ? t('vacation.select.details.book.b')
            : t('vacation.select.details.book')}
        </StyledButton>
      </div>
    </div>
  );
}

type RoomMobileHeaderProps = {
  roomsIncludedInPrice: number;
  roomName: string;
  roomsLeft: number;
} & React.HTMLAttributes<HTMLElement>;

function RoomMobileHeader(props: RoomMobileHeaderProps) {
  const { roomsIncludedInPrice, roomName, roomsLeft, ...rest } = props;

  return (
    <div
      css={`
        display: flex;
        flex-direction: column;
        gap: 4px;
      `}
      {...rest}
    >
      <div
        css={`
          display: flex;
          align-items: center;
          gap: 8px;
        `}
      >
        {roomsIncludedInPrice > 1 && <RoomsIncludedInPrice roomsIncludedInPrice={roomsIncludedInPrice} />}
        <Text variant="Title5">{roomName}</Text>
      </div>
      <RoomAvailabilityAlertLabel roomsLeft={roomsLeft} />
    </div>
  );
}

const Separator = styled.hr`
  margin: 4px 0;
  border: 0;
  border-top: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderDisabled};
  width: 100%;
`;
